import { app, v } from "modular-openscriptjs";
import { IdGenerator, Pipeline, Requester } from "./fotastart";
import { routes as backendRoutes } from "./backendRoutes";
import { GoogleAnalytics } from "./analytics";
import {
	authenticated,
	can,
	formatThousand,
	redirect,
} from "./helpers";

// Import Classes from their new home
import { Notice, NoticeWrapper } from "./classes/Notice";
import { NavItem } from "./classes/NavItem";
import { Middleware } from "./classes/Middleware";
import { MainActionData } from "./classes/MainActionData";

// resolve the current page
app().value("currentPageResolver", () => null);

export const resolveCurrentPage = () => {
	return app("currentPageResolver")();	
}

/**
 * @param {() => HTMLElement} callback
 */
export const resolveCurrentPageUsing = (callback) => {
	app().value("currentPageResolver", callback);
}


// Import Contexts
import { gc, uc, dc, pc } from "./ojs.contexts";

export { Notice, NoticeWrapper, NavItem, Middleware, MainActionData, gc, uc, dc, pc };

// Network Requests
export const api = new Requester();

// ID generator
export const _UNIQUE_ID_ = new IdGenerator();

export const STRINGS = {
	SUCCESS: "success",
	ERROR: "error",
	INFO: "info",
	WARNING: "warning",
	DEFAULT: "default",
	CAPA_SYMBOL: "Ç",
};

/**
 * All pages
 * @enum {string}
 */
export const PAGES = {
	HOME: "Home",
	MY_ORDERS: "My Orders",
	MY_PRODUCTS: "My Products",
	MY_ADDRESSES: "My Addresses",
	PAYMENT_REQUESTS: "Top-Up Requests",
	CART: "Cart",
	CHECKOUT: "Checkout",
	PROFILE: "Profile",
	EDIT_PROFILE: "Edit Profile",
	CHANGE_PASSWORD: "Change Password",
	FORGOT_PASSWORD: "Forgot Password",
	RESET_PASSWORD: "Reset Password",
	PAYMENT: "Make Payment",
	CONFIRM_PAYMENT: "Confirm Payment",
	ORDER_DETAILS: "Order Details",
	SIGNUP: "Sign up",
	LOGIN: "Login",
	FAQS: "Faqs",
	SETTINGS: "Settings",
	WALLET: "My Wallet",
	AWARDS: "My Rewards",
	FOLLOWERS: "Followers & Followings",
	VIEW_PRODUCT: "View Product",
	FEED: "Feed",
	BUSINESSES: "Businesses",
	TRANSFERS: "Capa Transfers",
	SEARCH: "Discover",
	PAGE_404: "Not Found",
	FINALIZE_ORDER: "Place Order",
	OTP: "Identity",
	HASHTAGS_LOCATION: "Hashtags Locations",
	SUBSCRIPTIONS: "My Subscriptions",
	AI_CREDITS: "My AI Credits"
};

/**
 * Call back functions called when
 * an action button is clicked. These
 * are buttons rendered in the main action form.
 */
export const ACTION_CALLBACKS = {};

/**
 * Callback functions called to get the name
 * do display on the action buttons in the 
 * Main action from.
 */
export const ACTION_NAME_CALLBACKS = {};

const $e = app("$e");
const h = app("h");

/**
 * Default response pipeline
 */
export const rPipeline = new Pipeline();

export const analytics = new GoogleAnalytics();

gc.root = h.dom.querySelector("#app-root");

// Declare initial navItems

export let bottomNavItems = [
	NavItem.new({
		icon: () => h.i({ class: "bi bi-house" }),
		route: `/`,
		text: PAGES.HOME,
		appHook: 'home',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-search" }),
		route: `/search`,
		text: "Search",
		appHook: 'search'
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-cart" }),
		route: `/cart`,
		pageName: PAGES.CART,
		text: PAGES.CART,
		appHook: 'cart'
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-clipboard-check" }),
		route: `/orders/history`,
		text: PAGES.MY_ORDERS,
		appHook: 'order-history'
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-three-dots" }),
		onclick: `app("sendEvent")('${$e.ui.needs.offcanvas.nav}')`,
		text: "More",
		appHook: 'more-options'
	}),
];

export let offcanvasNavItems = [
	NavItem.new({
		text: PAGES.PROFILE,
		route: "/users/profile",
		icon: () => h.i({ class: "bi bi-person" }),
		appHook: 'profile',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-shop" }),
		route: "/businesses",
		text: PAGES.BUSINESSES,
		appHook: 'businesses',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-gem" }),
		route: `/users/subscriptions`,
		text: PAGES.SUBSCRIPTIONS,
		appHook: 'subscriptions',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-map" }),
		onclick: `app("sendEvent")('${$e.deliveryAddress.needs.addressList}')`,
		text: "My Addresses",
		appHook: 'my-addresses',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-wallet" }),
		route: `/wallet`,
		text: PAGES.WALLET,
		appHook: 'wallet',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-stars" }),
		route: `/users/ai-credits`,
		text: PAGES.AI_CREDITS,
		appHook: 'ai-credits',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-gear" }),
		route: `/settings`,
		text: PAGES.SETTINGS,
		appHook: 'settings',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-award" }),
		route: `/awards`,
		text: PAGES.AWARDS,
		appHook: 'awards',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-power" }),
		onclick: `app("sendEvent")('${$e.user.needs.logout}')`,
		text: "Logout",
		appHook: 'logout',
	}),

	NavItem.new({
		icon: () => h.i({ class: "fa-solid fa-power-off" }),
		onclick: `app("sendEvent")('${$e.user.needs.authentication}')`,
		text: "Login",
		condition: () => !authenticated(),
		appHook: 'login',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-person-plus" }),
		route: `users/sign-up`,
		text: "Sign Up",
		condition: () => !authenticated(),
		appHook: 'sign-up',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-geo-alt" }),
		route: `admin.hashtag-locations`,
		condition: () => can("add-hashtag-location"),
		text: PAGES.HASHTAGS_LOCATION,
		appHook: 'hashtag-locations',
	}),
];

export let sideNavItems = [
	NavItem.new({
		icon: () => h.i({ class: "bi bi-house-door" }),
		route: `/`,
		text: PAGES.HOME,
		appHook: 'home',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-search" }),
		route: `/search`,
		text: PAGES.SEARCH,
		appHook: 'search',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-cart" }),
		route: "/cart",
		text: PAGES.CART,
		appHook: 'cart',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-clipboard-check" }),
		route: `/orders/history`,
		text: PAGES.MY_ORDERS,
		appHook: 'order-history',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-shop" }),
		route: "/businesses",
		text: PAGES.BUSINESSES,
		appHook: 'businesses',
	}),

	NavItem.new({
		text: PAGES.PROFILE,
		onclick: h.func(`app("router").to`, "user.view-profile", {
			user: "current",
		}),
		icon: () => h.i({ class: "bi bi-person" }),
		appHook: 'profile',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-gem" }),
		route: `/users/subscriptions`,
		text: PAGES.SUBSCRIPTIONS,
		appHook: 'subscriptions',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-map" }),
		onclick: `app("sendEvent")('${$e.deliveryAddress.needs.addressList}')`,
		text: "My Addresses",
		appHook: 'my-addresses',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-stars" }),
		route: `/users/ai-credits`,
		text: PAGES.AI_CREDITS,
		appHook: 'ai-credits',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-award" }),
		route: `/awards`,
		text: PAGES.AWARDS,
		appHook: 'awards',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-geo-alt" }),
		route: `admin.hashtag-locations`,
		condition: () => can("add-hashtag-location"),
		text: PAGES.HASHTAGS_LOCATION,
		appHook: 'hashtag-locations',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-gear" }),
		route: `/settings`,
		text: PAGES.SETTINGS,
		appHook: 'settings',
	}),

	NavItem.new({
		icon: () => h.i({ class: "bi bi-power" }),
		onclick: `app("sendEvent")('${$e.user.needs.logout}')`,
		text: "Logout",
		appHook: 'logout',
	}),
];

export const routes = backendRoutes;